import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/browser';

import { ocppService } from '../services/ocpp/OcppService';
import { UserSessionResponse } from '../services/ocpp/types';

/** Nothing pushes session starts to the client, so the live view polls. */
export const ACTIVE_SESSIONS_POLL_MS = 10000;

export const ACTIVE_SESSIONS_QUERY_KEY = ['sessions', 'active'];

const EMPTY: UserSessionResponse[] = [];

/**
 * Every charge the account currently has running, newest first. An account can
 * charge in two places at once, so callers get the whole set and pick the one
 * their screen is about.
 *
 * Several screens watch this at the same time — Home and the bottom navigation
 * beneath it — so it is a shared query rather than a poll per component.
 */
export const useActiveSessions = () => {
  const { data } = useQuery({
    queryKey: ACTIVE_SESSIONS_QUERY_KEY,
    queryFn: async () => {
      try {
        return await ocppService.getActiveSessions();
      } catch (error) {
        // Offline or a failed poll — the screen shows nothing charging rather
        // than freezing on a stale session, but the charging tab disappearing
        // should not be silent.
        Sentry.captureException({
          message: '[ocppService][getActiveSessions]',
          error,
        });
        return EMPTY;
      }
    },
    refetchInterval: ACTIVE_SESSIONS_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });

  return { sessions: data ?? EMPTY };
};
